import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SUPPORTED_COUNTRIES, isSupportedCountry } from '@boletra/shared';
import { isSupportedLocale, type Locale } from '@boletra/i18n';
import { Layout } from '../../components/Layout.js';
import { api, ApiClientError } from '../../lib/api.js';
import { useAuthStore } from '../../lib/auth.js';

interface CreateOrgResponse {
  id: string;
  name: string;
  slug: string;
  stripeOnboardingComplete: boolean;
}

interface GeoHintResponse {
  country: string | null;
}

function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

export function OnboardingPage() {
  const { lang } = useParams();
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  // El URL es la fuente confiable del locale del usuario (`/es/...` o
  // `/en/...`). `i18n.language` puede llegar como `'es-419'`, `'en-US'` u
  // otra variante regional antes de que `LocaleGuard` normalice — y el
  // server valida estrictamente contra `localeSchema` (`'es' | 'en'`), así
  // que cualquier otro valor explotaría como 422.
  const locale: Locale = pickLocale(lang, i18n.language);
  const hydrate = useAuthStore((s) => s.hydrate);
  const sessionUser = useAuthStore((s) => s.user);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);
  // null hasta que sepamos algo concreto. Forzamos al usuario a confirmar
  // (o al menos a NO dejar el default histórico de "MX" que rompía a todos
  // los productores fuera de México en el flow de Stripe Connect).
  const [country, setCountry] = useState<string | null>(null);
  const [touchedCountry, setTouchedCountry] = useState(false);

  // 1. Si el usuario ya viene con `country` capturado en signup, lo usamos.
  // 2. Si no (cuentas viejas o CF no resolvió IP), pedimos /auth/geo-hint
  //    para resolverlo en este request — sirve cuando el signup pasó por
  //    una VPN y ahora el usuario entró desde su IP real.
  // 3. Si todo falla, dejamos el select vacío y el usuario lo elige a mano.
  const geoHint = useQuery<GeoHintResponse>({
    queryKey: ['geo-hint'],
    queryFn: () => api<GeoHintResponse>('/auth/geo-hint'),
    enabled: !sessionUser?.country,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (touchedCountry) return;
    const suggested = sessionUser?.country ?? geoHint.data?.country ?? null;
    if (!suggested) return;
    // Solo pre-seleccionamos si Stripe Connect soporta ese país. Si la IP
    // resolvió a algo como BO (Bolivia, sin Stripe Connect), no llenamos
    // el select para no forzar al usuario a abrir un account que va a fallar.
    if (isSupportedCountry(suggested)) {
      setCountry(suggested.toUpperCase());
    }
  }, [sessionUser?.country, geoHint.data?.country, touchedCountry]);

  const mutation = useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      defaultLocale: 'es' | 'en';
      country: string;
    }) => api<CreateOrgResponse>('/orgs', { method: 'POST', body: input }),
    onSuccess: async () => {
      await hydrate();
      navigate(`/${lang}/dashboard`, { replace: true });
    },
  });

  const effectiveSlug = touchedSlug ? slug : slugify(name);
  const canSubmit =
    name.trim().length >= 2 &&
    effectiveSlug.length >= 2 &&
    !!country &&
    country.length === 2 &&
    !mutation.isPending;

  // Mensaje sutil para el usuario explicando de dónde sale el default.
  // Le decimos solo si efectivamente pre-seleccionamos algo automáticamente.
  const autoFilled = !touchedCountry && country !== null;

  const countryOptions = useMemo(() => SUPPORTED_COUNTRIES, []);

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">{t('onboarding_page.title')}</h1>
        <p className="text-neutral-400 mb-8 leading-relaxed">{t('onboarding_page.subtitle')}</p>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit || !country) return;
            mutation.mutate({
              name: name.trim(),
              slug: effectiveSlug,
              defaultLocale: locale,
              country,
            });
          }}
        >
          <label className="block">
            <span className="text-sm text-neutral-400">{t('onboarding_page.name_label')}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('onboarding_page.name_placeholder')}
              required
              minLength={2}
              maxLength={80}
              className="input mt-2"
            />
          </label>
          <label className="block">
            <span className="text-sm text-neutral-400">{t('onboarding_page.slug_label')}</span>
            <div className="mt-2 flex">
              <span className="inline-flex items-center px-3 text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 border-r-0 rounded-l-lg">
                boletra.com/o/
              </span>
              <input
                type="text"
                value={effectiveSlug}
                onChange={(e) => {
                  setTouchedSlug(true);
                  setSlug(slugify(e.target.value));
                }}
                pattern="[a-z0-9-]+"
                placeholder="mi-productora"
                required
                minLength={2}
                maxLength={50}
                className="input rounded-l-none flex-1"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">{t('onboarding_page.slug_help')}</p>
          </label>
          <label className="block">
            <span className="text-sm text-neutral-400">{t('onboarding_page.country_label')}</span>
            <select
              value={country ?? ''}
              onChange={(e) => {
                setTouchedCountry(true);
                setCountry(e.target.value || null);
              }}
              required
              className="input mt-2"
            >
              <option value="" disabled>
                {t('onboarding_page.country_select_placeholder')}
              </option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name[locale]}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">{t('onboarding_page.country_help')}</p>
            {autoFilled && (
              <p className="text-xs text-[var(--color-accent)] mt-1">
                {t('onboarding_page.country_detected_help')}
              </p>
            )}
          </label>
          <button type="submit" disabled={!canSubmit} className="btn-primary w-full">
            {mutation.isPending ? t('onboarding_page.creating') : t('onboarding_page.submit')}
          </button>
          {mutation.isError && (
            <p className="text-red-400 text-sm">{(mutation.error as ApiClientError).message}</p>
          )}
        </form>
      </div>
    </Layout>
  );
}

/**
 * Resuelve el locale del usuario probando candidatos en orden de confianza:
 * URL (`/es/...` / `/en/...`) → `i18n.language` (base BCP-47) → `'es'`.
 * Cualquier variante regional se reduce al código base antes de validarse.
 */
function pickLocale(...candidates: Array<string | undefined>): Locale {
  for (const raw of candidates) {
    if (!raw) continue;
    const base = raw.split('-')[0]?.toLowerCase();
    if (base && isSupportedLocale(base)) return base;
  }
  return 'es';
}
